'use client';

import React from 'react';
import { AlertTriangle } from 'lucide-react';

export interface DuplicateWarning {
  id: string;
  title: string;
  match_confidence: string;
  match_reason: string;
  grand_total: number;
  status: string;
  created_at: string;
}

export interface DuplicateWarningBannerProps {
  duplicateChecking: boolean;
  duplicateWarning: DuplicateWarning[] | null;
  acknowledged: boolean;
  onAcknowledge: () => void;
  onDismiss: () => void;
}

export function DuplicateWarningBanner({
  duplicateChecking,
  duplicateWarning,
  acknowledged,
  onAcknowledge,
  onDismiss,
}: DuplicateWarningBannerProps) {
  if (duplicateChecking) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-900/20">
        <span className="text-sm text-blue-700 dark:text-blue-300">Checking for duplicate expenses…</span>
      </div>
    );
  }

  if (!duplicateWarning || duplicateWarning.length === 0) return null;

  const borderClass = acknowledged
    ? 'border-amber-300 dark:border-amber-700'
    : 'border-red-400 dark:border-red-700';

  return (
    <div className={`rounded-lg ${borderClass} bg-amber-50 p-3 dark:bg-amber-900/20`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            Possible duplicate expense detected
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
            {acknowledged
              ? 'You acknowledged this warning. You can proceed with saving.'
              : 'This receipt may have already been submitted. Review the matches below — you must acknowledge before saving.'}
          </p>
          <div className="mt-2 space-y-1.5">
            {duplicateWarning.map((dup) => (
              <div key={dup.id} className="rounded-md bg-white/60 dark:bg-gray-800/60 px-2.5 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                    {dup.title}
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    dup.match_confidence === 'high'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}>
                    {dup.match_confidence}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {dup.match_reason}
                </div>
                <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                  R{Number(dup.grand_total).toFixed(2)} • {dup.status} • {new Date(dup.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3">
            {!acknowledged && (
              <button
                type="button"
                onClick={onAcknowledge}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
              >
                Acknowledge & Continue
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
            >
              {acknowledged ? 'Dismiss' : 'Cancel entry'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
